import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

// Select best media candidate by viewport width and device pixel ratio
// imageMedia item shape example:
// { path, alt, altKey, ppi: "1", viewport: { name, width, maxWidth }, background }

const getDevicePixelRatioSafe = () => {
    if (typeof window === 'undefined') return 1;
    return Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
};

const getViewportWidthSafe = () => {
    if (typeof window === 'undefined') return 0;
    return window.innerWidth || 0;
};

const matchesViewport = (candidate, width) => {
    const min = candidate?.viewport?.width ?? 0;
    const max = candidate?.viewport?.maxWidth;

    if (typeof max === 'number' && !Number.isNaN(max)) {
        return width >= min && width <= max;
    }
    return width >= min;
};

const preferPpi = (candidates, dpr) => {
    if (!Array.isArray(candidates) || !candidates.length) return undefined;

    // Normalize and sort by numeric ppi asc
    const sorted = [...candidates].sort((a, b) => (parseFloat(a.ppi) || 1) - (parseFloat(b.ppi) || 1));

    // Try to find best match: first ppi >= dpr, else the largest available below dpr
    let best = sorted.find((x) => (parseFloat(x.ppi) || 1) >= dpr);

    if (!best) best = sorted[sorted.length - 1];
    return best;
};

const isDefaultMedia = (x) => x?.viewport?.name === 'Default';

const getDefaultCandidates = (imageMedia) => {
    return (imageMedia || []).filter((x) => isDefaultMedia(x));
};

const useBestImageMedia = (imageMedia) => {
    const [viewportWidth, setViewportWidth] = useState(getViewportWidthSafe());
    const [dpr, setDpr] = useState(getDevicePixelRatioSafe());
    const rafRef = useRef();
    const resizeTimeoutRef = useRef();

    const onResize = useCallback(() => {
        // Debounce using rAF + timeout to avoid frequent recalculations
        if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = setTimeout(() => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                setViewportWidth(getViewportWidthSafe());
                setDpr(getDevicePixelRatioSafe());
            });
        }, 120);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.addEventListener('resize', onResize);
        // Some browsers can change DPR on zoom; listen to media query changes as a hint
        const mq = window.matchMedia(`(resolution: ${Math.round(getDevicePixelRatioSafe() * 96)}dpi)`);
        const mqListener = () => setDpr(getDevicePixelRatioSafe());

        if (mq && mq.addEventListener) mq.addEventListener('change', mqListener);

        return () => {
            window.removeEventListener('resize', onResize);
            if (mq && mq.removeEventListener) mq.removeEventListener('change', mqListener);
            if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [onResize]);

    const best = useMemo(() => {
        if (!Array.isArray(imageMedia) || imageMedia.length === 0) return undefined;

        // 1) Find all candidates matching current viewport
        const viewportMatches = imageMedia
            .filter((x) => matchesViewport(x, viewportWidth))
            .filter((x) => !isDefaultMedia(x));

        // 2) If none match, fallback to default candidates
        const candidates = viewportMatches.length ? viewportMatches : getDefaultCandidates(imageMedia);

        if (!candidates.length) return undefined;

        // 3) Choose by ppi relative to dpr
        const result = preferPpi(candidates, dpr);

        return result;
    }, [imageMedia, viewportWidth, dpr]);

    return best;
};

export default useBestImageMedia;
